import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["locale", "activeItem", "navItemsMap", "workspaceOptions", "activeWorkspace", "translations", "zendeskProductKeys", "helpMenuItems", "profileMenuItems", "subnavTitle", "subnavItems", "subnavActionTooltip", "subnavActionMenuItems", "hideToggleSubnavButton", "globalBanners", "hasFlagManager"]

import { ref, onMounted } from 'vue'
import { useEventListener } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import type {
  HelpMenuKey,
  NavigationKey,
  NavItem,
  WorkspaceOption,
  HelpMenuItem,
  SubnavItem,
  ProfileMenuKey,
  ProductKey,
  SubnavActionMenuItem,
  BannerItems,
  BannerActionKey,
  SearchResultType,
} from '@klausapp/app-navigation/types'
import { ZUI_EVENTS } from '@klausapp/app-navigation/src/utils/event'

import { hasZendeskProxy } from '@/config'
import { getLocale } from '@/i18n'
import { getZendeskProducts } from '@/modules/shared/Navigation/components/ZendeskProductTray/api'
import Application from './Application.vue'

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'AppWrapper',
  props: {
    activeItem: {},
    navItemsMap: {},
    workspaceOptions: {},
    activeWorkspace: {},
    translations: {},
    helpMenuItems: {},
    subnavTitle: {},
    subnavItems: {},
    subnavActionTooltip: {},
    profileMenuItems: {},
    subnavActionMenuItems: {},
    hideToggleSubnavButton: { type: Boolean },
    globalBanners: {},
    hasFlagManager: { type: Boolean }
  },
  emits: ["nav-item-click", "workspace-click", "help-menu-item-click", "profile-menu-click", "subnav-item-click", "subnav-item-action-click", "subnav-extra-click", "subnav-action-click", "banner-click", "search-query-change", "search-result-select"],
  setup(__props: any, { emit: __emit }) {



// Emits
const emit = __emit

// Reactive state
const locale = getLocale()
const isMounted = ref(false)

const { data: zendeskProductKeys } = useQuery({
  queryKey: ['active-zendesk-products'],
  queryFn: getZendeskProducts,
  select: (data) => data.products,
  enabled: hasZendeskProxy,
  placeholderData: () => ({
    features: { product_tray_enable_list_view: true },
    products: [{ key: 'quality_assurance' as ProductKey }],
  }),
})

// Utility functions
const waitForElement = (selector: string, container: Element | ShadowRoot, timeout = 5000): Promise<Element> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const tryToFind = (): void => {
      const element = container.querySelector(selector)

      if (element) {
        return resolve(element)
      }

      if (Date.now() - startTime > timeout) {
        return reject(new Error(`[ZUI] Element "${selector}" not found after ${timeout}ms`))
      }

      requestAnimationFrame(tryToFind)
    }

    tryToFind()
  })
}

const setupEventListeners = (element: HTMLElement | Element): void => {
  if (element) {
    useEventListener(element, ZUI_EVENTS.WORKSPACE_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.WORKSPACE_CLICK, event.detail.id)
    })

    useEventListener(element, ZUI_EVENTS.HELP_MENU_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.HELP_MENU_ITEM_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.PROFILE_MENU_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.PROFILE_MENU_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.NAV_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.NAV_ITEM_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ITEM_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ITEM_CLICK, event.detail.navItem, event.detail.subnavItem)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ITEM_ACTION_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ITEM_ACTION_CLICK, event.detail.navItem, event.detail.subnavItem, event.detail.action)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_EXTRA_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_EXTRA_CLICK, event.detail.navItem, event.detail.extraItem)
    })

    useEventListener(element, ZUI_EVENTS.SUBNAV_ACTION_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SUBNAV_ACTION_CLICK, event.detail.key)
    })

    useEventListener(element, ZUI_EVENTS.BANNER_CLICK, (event: CustomEvent) => {
      emit(ZUI_EVENTS.BANNER_CLICK, event.detail.id, event.detail.action)
    })

    // Search events
    useEventListener(element, ZUI_EVENTS.SEARCH_QUERY_CHANGE, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SEARCH_QUERY_CHANGE, event.detail.query)
    })

    useEventListener(element, ZUI_EVENTS.SEARCH_RESULT_SELECT, (event: CustomEvent) => {
      emit(ZUI_EVENTS.SEARCH_RESULT_SELECT, event.detail.type, event.detail.id, event.detail.data)
    })
  }
}

const initializeNavigation = async (): Promise<void> => {
  try {
    // Wait for custom element to be defined
    await customElements.whenDefined('zui-navigation')

    const zuiNavigationHost = document.querySelector('zui-navigation')

    if (!zuiNavigationHost) {
      throw new Error('[ZUI] Could not find zui-navigation element')
    }

    // Setup event listeners
    setupEventListeners(zuiNavigationHost)

    // Wait for the mount point to be ready
    await waitForElement('#vue-app-mount', zuiNavigationHost)

    // Enable Teleport to render Application
    isMounted.value = true
  } catch (error) {
    console.error('[ZUI] Error during navigation initialization:', error)
  }
}

// Lifecycle hooks
onMounted(() => {
  initializeNavigation()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createCommentVNode(" eslint-disable-next-line vue/no-undef-components "),
    _createElementVNode("zui-navigation", {
      locale: _unref(locale),
      activeItem: _ctx.activeItem,
      navItemsMap: _ctx.navItemsMap,
      workspaceOptions: _ctx.workspaceOptions,
      activeWorkspace: _ctx.activeWorkspace,
      translations: _ctx.translations,
      zendeskProductKeys: _unref(zendeskProductKeys),
      helpMenuItems: _ctx.helpMenuItems,
      profileMenuItems: _ctx.profileMenuItems,
      subnavTitle: _ctx.subnavTitle,
      subnavItems: _ctx.subnavItems,
      subnavActionTooltip: _ctx.subnavActionTooltip,
      subnavActionMenuItems: _ctx.subnavActionMenuItems,
      hideToggleSubnavButton: _ctx.hideToggleSubnavButton,
      globalBanners: _ctx.globalBanners,
      hasFlagManager: _ctx.hasFlagManager
    }, null, 8 /* PROPS */, _hoisted_1),
    (isMounted.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "#vue-app-mount"
        }, [
          _createVNode(Application)
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})